<template>
  <li class="navigation-second-level has-third-level-links is-my-pension-fund">
    <template v-if="hasSelectedEmployer">
      <nuxt-link :to="employer?.url"
        >{{ $texts('myEmployer', 'My employer') }}:<br />{{
          employer?.title
        }}</nuxt-link
      >
      <ul class="mt-20">
        <li v-for="link in employerLinks" :key="link.title">
          <nuxt-link :to="link.to">{{ link.title }}</nuxt-link>
        </li>
      </ul>
      <NuxtLink
        :to="employerUrl"
        class="button is-primary has-text-weight-bold mt-20 self-start"
        >{{ $texts('myContactPerson', 'My contact persons') }}</NuxtLink
      >
    </template>
    <template v-else>
      <div class="space-y-20">
        <h3 class="font-semibold tracking-wide text-white md:text-gray-600">
          {{ $texts('chooseEmployer', 'Choose employer') }}
        </h3>
        <p class="text-white md:text-gray-900">
          {{
            $texts(
              'chooseEmployerLead',
              'Select your employer so that we can show you suitable information and contact persons.',
            )
          }}
        </p>
        <div>
          <Combobox
            v-model="selectedEmployer"
            as="div"
            @update:model-value="query = ''"
          >
            <div class="relative">
              <ComboboxInput
                class="block w-full rounded-large border border-gray-300 bg-white py-12 pl-16 pr-48 text-base text-gray-900 placeholder:text-gray-400 focus:border-blue-700"
                :display-value="(employer: any) => employer?.title"
                :placeholder="$texts('selectEmployer', 'Select employer')"
                @change="query = $event.target.value"
              />
              <ComboboxButton
                class="absolute inset-y-0 right-0 z-10 flex h-full w-48 cursor-pointer items-center justify-center text-gray-600 hover:text-gray-900"
              >
                <SpriteSymbol name="nav-arrow-down" class="size-24" />
              </ComboboxButton>

              <ComboboxOptions
                v-if="filteredEmployers.length > 0 || query.length > 0"
                class="absolute z-10 mt-8 w-full overflow-hidden rounded-large border border-gray-300 bg-white"
              >
                <div class="max-h-[240px] overflow-auto py-8">
                  <ComboboxOption
                    v-for="e in filteredEmployers"
                    :key="e.id"
                    v-slot="{ active, selected }"
                    :value="e"
                    as="template"
                  >
                    <li
                      :class="[
                        'relative cursor-pointer select-none px-16 py-12 text-base',
                        active
                          ? 'bg-blue-700 text-white'
                          : 'text-gray-900 hover:bg-gray-50',
                      ]"
                    >
                      <span
                        :class="[
                          'block truncate',
                          selected
                            ? 'font-semibold tracking-wide'
                            : 'font-regular',
                        ]"
                      >
                        {{ e.title }}
                      </span>
                    </li>
                  </ComboboxOption>
                  <div
                    v-if="filteredEmployers.length === 0"
                    class="px-16 py-12 text-base text-gray-400"
                  >
                    {{ $texts('noResults', 'No results found') }}
                  </div>
                </div>
              </ComboboxOptions>
            </div>
          </Combobox>
        </div>
        <div>
          <button
            :to="{ name: 'choose-employer' }"
            class="button is-primary is-fullwidth mt-4 self-start"
            @click="setEmployerId(selectedEmployer?.id)"
          >
            {{ $texts('confirm', 'Confirm') }}
          </button>
        </div>
      </div>
    </template>
  </li>
</template>

<script lang="ts" setup>
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from '@headlessui/vue'
import { notNullish } from '@vueuse/core'

const { $texts } = useNuxtApp()

interface Employer {
  id: string
  title: string
  url: string
  selected: boolean
}

const {
  employerUrl,
  hasSelectedEmployer,
  employer,
  employerLinks,
  employerId,
  setEmployerId,
} = useVisitorState()

const query = ref('')
const selectedEmployer = ref(null as Employer | null)
const filteredEmployers = computed(() =>
  query.value === ''
    ? employers.value || []
    : employers.value?.filter((employer) => {
        return employer.title.toLowerCase().includes(query.value.toLowerCase())
      }) || [],
)

const { data: employers } = await useAsyncData(() =>
  useGraphqlQuery(
    'searchEmployer',
    { text: '' },
    {
      graphqlCaching: {
        client: true,
      },
    },
  ).then((v) =>
    (v.data.searchContent?.hits || [])
      .map((v) =>
        v && v.source && v.source.title && v.source.id && v.source.url
          ? {
              id: v.source.id,
              title: v.source.title,
              url: v.source.url,
              selected: v.source.id === employerId.value,
            }
          : null,
      )
      .filter(notNullish),
  ),
)
</script>

<style lang="postcss">
.navigation-second-level.is-my-pension-fund {
  @apply flex flex-col;
  @screen md {
    flex: 0 0 20rem;
    @apply rounded-br-huge bg-blue-50;
  }
  @screen xl {
    flex: 0 0 var(--navigation-content-addon-width);
  }
}
</style>
